import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import api from '../services/api';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useContext(CartContext);
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    direccion: '',
    ciudad: '',
    estado: '',
    cp: ''
  });

  const [envio, setEnvio] = useState({
    cotizando: false,
    cotizaciones: [],
    seleccionado: null,
    error: null
  });

  const [comprobante, setComprobante] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderData, setOrderData] = useState(null);

  const subtotal = cartTotal;
  const pesoTotal = cartItems.reduce((acc, item) => acc + (item.quantity * 0.5), 0); // 0.5kg por producto

  // Cotizar cuando cambia el CP
  useEffect(() => {
    const cotizarEnvio = async () => {
      if (formData.cp && formData.cp.length === 5) {
        setEnvio(prev => ({ ...prev, cotizando: true, error: null }));
        
        try {
          const response = await api.post('/envios/cotizar', {
            cp_destino: formData.cp,
            peso: Math.max(1, pesoTotal),
            valor_declarado: subtotal,
            largo: 30,
            alto: 20,
            ancho: 15,
            nombre: `${formData.nombre} ${formData.apellido}`,
            telefono: formData.telefono,
            direccion: formData.direccion,
            ciudad: formData.ciudad,
            estado: formData.estado
          });

          if (response.data.success) {
            setEnvio(prev => ({
              ...prev,
              cotizando: false,
              cotizaciones: response.data.data.cotizaciones,
              seleccionado: response.data.data.mas_economica
            }));
          } else {
            setEnvio(prev => ({
              ...prev,
              cotizando: false,
              error: response.data.error || 'No se pudo cotizar'
            }));
          }
        } catch (error) {
          setEnvio(prev => ({
            ...prev,
            cotizando: false,
            error: 'Error al conectar con el servidor'
          }));
        }
      }
    };

    const timer = setTimeout(() => {
      if (formData.cp?.length === 5) {
        cotizarEnvio();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.cp, pesoTotal, subtotal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setComprobante(e.target.files[0]);
  };

  const seleccionarEnvio = (opcion) => {
    setEnvio(prev => ({ ...prev, seleccionado: opcion }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!envio.seleccionado) {
      alert('Por favor selecciona una opción de envío');
      return;
    }
    if (!comprobante) {
      alert('Debes subir el comprobante de pago');
      return;
    }

    setProcesando(true);

    try {
      // 1. Subir comprobante
      const formDataUpload = new FormData();
      formDataUpload.append('comprobante', comprobante);

      const uploadRes = await api.post('/upload/comprobante', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!uploadRes.data.success) {
        throw new Error('Error al subir comprobante');
      }

      const comprobanteUrl = uploadRes.data.url;

      // 2. Crear venta
      const pedidoData = {
        cliente: {
          nombre: `${formData.nombre} ${formData.apellido}`,
          email: formData.email,
          telefono: formData.telefono,
          direccion: formData.direccion,
          ciudad: formData.ciudad,
          estado: formData.estado,
          cp: formData.cp,
          pais: 'MX'
        },
        productos: cartItems.map(item => ({
          id: item.id,
          nombre: item.name,
          cantidad: item.quantity,
          precio: item.price
        })),
        subtotal: subtotal,
        envio: {
          carrier: envio.seleccionado.carrier,
          service: envio.seleccionado.service,
          carrierId: envio.seleccionado.carrierId,
          serviceId: envio.seleccionado.serviceId,
          precio: envio.seleccionado.price,
          pesoTotal: pesoTotal,
          cp_destino: formData.cp
        },
        total: subtotal + envio.seleccionado.price,
        metodo_pago: 'transferencia',
        comprobante_url: comprobanteUrl
      };

      console.log('📦 Enviando pedido:', pedidoData);

      const response = await api.post('/ventas', pedidoData);

      if (response.data.success) {
        setOrderData({
          folio: response.data.folio,
          guia: response.data.guia,
          total: subtotal + envio.seleccionado.price
        });
        setOrderPlaced(true);
        clearCart();
        setTimeout(() => navigate('/products'), 5000);
      } else {
        alert('Error al procesar el pedido');
      }
    } catch (error) {
      console.error('❌ Error en checkout:', error);
      alert('Ocurrió un error: ' + (error.response?.data?.error || error.message));
    } finally {
      setProcesando(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="checkout-empty">
        <h2>No hay productos en el carrito</h2>
        <button onClick={() => navigate('/products')}>Ver productos</button>
      </div>
    );
  }

  if (orderPlaced) {
    return (
      <div className="order-confirmation">
        <div className="confirmation-content">
          <div className="success-icon">✓</div>
          <h2>¡Pedido Confirmado!</h2>
          {orderData && (
            <>
              <p><strong>Folio:</strong> {orderData.folio}</p>
              {orderData.guia && <p><strong>Guía:</strong> {orderData.guia}</p>}
              <p><strong>Total:</strong> ${orderData.total.toFixed(2)} MXN</p>
            </>
          )}
          <p>Te redirigiremos a la tienda en unos segundos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <h1>Finalizar Compra</h1>
      <div className="checkout-container">
        <form onSubmit={handleSubmit} className="checkout-form">
          {/* Datos personales */}
          <div className="form-section">
            <h2>Datos personales</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Nombre *</label>
                <input type="text" name="nombre" value={formData.nombre} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Apellido *</label>
                <input type="text" name="apellido" value={formData.apellido} onChange={handleInputChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Teléfono *</label>
                <input type="tel" name="telefono" value={formData.telefono} onChange={handleInputChange} required />
              </div>
            </div>
          </div>

          {/* Dirección */}
          <div className="form-section">
            <h2>Dirección de envío</h2>
            <div className="form-group">
              <label>Dirección (calle y número) *</label>
              <input type="text" name="direccion" value={formData.direccion} onChange={handleInputChange} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Ciudad *</label>
                <input type="text" name="ciudad" value={formData.ciudad} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Estado *</label>
                <select name="estado" value={formData.estado} onChange={handleInputChange} required className="form-select">
                  <option value="">Selecciona un estado</option>
                  <option value="AGS">Aguascalientes</option>
                  <option value="BC">Baja California</option>
                  <option value="BCS">Baja California Sur</option>
                  <option value="CAMP">Campeche</option>
                  <option value="COAH">Coahuila</option>
                  <option value="COL">Colima</option>
                  <option value="CHIS">Chiapas</option>
                  <option value="CHIH">Chihuahua</option>
                  <option value="CDMX">Ciudad de México</option>
                  <option value="DGO">Durango</option>
                  <option value="GTO">Guanajuato</option>
                  <option value="GRO">Guerrero</option>
                  <option value="HGO">Hidalgo</option>
                  <option value="JAL">Jalisco</option>
                  <option value="MEX">Estado de México</option>
                  <option value="MICH">Michoacán</option>
                  <option value="MOR">Morelos</option>
                  <option value="NAY">Nayarit</option>
                  <option value="NL">Nuevo León</option>
                  <option value="OAX">Oaxaca</option>
                  <option value="PUE">Puebla</option>
                  <option value="QRO">Querétaro</option>
                  <option value="QROO">Quintana Roo</option>
                  <option value="SLP">San Luis Potosí</option>
                  <option value="SIN">Sinaloa</option>
                  <option value="SON">Sonora</option>
                  <option value="TAB">Tabasco</option>
                  <option value="TAMPS">Tamaulipas</option>
                  <option value="TLAX">Tlaxcala</option>
                  <option value="VER">Veracruz</option>
                  <option value="YUC">Yucatán</option>
                  <option value="ZAC">Zacatecas</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Código Postal *</label>
                <input
                  type="text"
                  name="cp"
                  value={formData.cp}
                  onChange={handleInputChange}
                  maxLength="5"
                  pattern="[0-9]{5}"
                  required
                  className={envio.cotizaciones.length > 0 ? 'valido' : envio.error ? 'error' : ''}
                />
                {envio.cotizando && <small className="cotizando">Cotizando envío...</small>}
                {envio.error && <small className="error">{envio.error}</small>}
              </div>
            </div>

            {/* Opciones de envío */}
            {envio.cotizaciones.length > 0 && (
              <div className="opciones-envio">
                <h3>Opciones de envío</h3>
                {envio.cotizaciones.map((opcion, idx) => (
                  <div
                    key={idx}
                    className={`opcion-envio ${envio.seleccionado === opcion ? 'seleccionado' : ''}`}
                    onClick={() => seleccionarEnvio(opcion)}
                  >
                    <div className="opcion-header">
                      <input
                        type="radio"
                        name="envio"
                        checked={envio.seleccionado === opcion}
                        onChange={() => seleccionarEnvio(opcion)}
                      />
                      <span className="carrier">{opcion.carrier}</span>
                      <span className="servicio">{opcion.description}</span>
                    </div>
                    <div className="opcion-body">
                      <span className="precio">${opcion.price} MXN</span>
                      <span className="tiempo">{opcion.estimatedDays}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Método de pago - solo transferencia */}
          <div className="form-section">
            <h2>Método de pago</h2>
            <div className="payment-methods">
              <label className="payment-method active">
                <input type="radio" name="metodoPago" value="transferencia" checked={true} readOnly />
                <span>Transferencia bancaria</span>
              </label>
            </div>
            <div className="form-group">
              <label>Sube tu comprobante de pago (imagen o PDF)</label>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileChange}
                required
              />
              <small>El comprobante será revisado por el administrador para confirmar el pago.</small>
            </div>
          </div>

          <button type="submit" className="place-order-btn" disabled={procesando || !envio.seleccionado}>
            {procesando ? 'Procesando...' : 'Confirmar pedido'}
          </button>
        </form>

        {/* Resumen del pedido */}
        <div className="order-summary">
          <h2>Resumen</h2>
          <div className="summary-items">
            {cartItems.map(item => (
              <div key={item.id} className="summary-item">
                <span>{item.name} x{item.quantity}</span>
                <span>${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="summary-totals">
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="summary-row envio">
              <span>Envío:</span>
              <span>
                {envio.seleccionado ? `$${envio.seleccionado.price} (${envio.seleccionado.carrier})` : 'Por cotizar'}
              </span>
            </div>
            <div className="summary-row total">
              <span>Total:</span>
              <span>${(subtotal + (envio.seleccionado?.price || 0)).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;